/**
 * Portal Layout — simpler chrome for client users
 */

import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useMatch, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import coreskanLogo from '../../assets/coreskan-logo.png';
import type { Client } from '../../../shared/types/auth';
import './portal-responsive.css';

function getInitials(user: { name?: string | null; email?: string } | null) {
  return (user?.name || user?.email || '?')
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('');
}

export function PortalLayout() {
  const { user, logout } = useAuth();
  const isViewer = useMatch('/portal/projects/:id');
  const location = useLocation();
  const [client, setClient] = useState<Client | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.portal.getClient()
      .then((data) => { console.log('[PortalLayout] client data:', data); setClient(data); })
      .catch((err) => console.error('[PortalLayout] Failed to fetch client:', err));
  }, []);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  return (
    <div style={styles.container}>
      {!isViewer && (
        <header className="portal-header" ref={menuRef}>
          <div className="portal-header__left">
            <img src={coreskanLogo} alt="CoreSkan" className="portal-header__logo" />
            <nav className="portal-header__nav portal-header__nav--desktop">
              <NavLink
                to="/portal/projects"
                className="portal-header__nav-link"
                style={({ isActive }) => ({ color: isActive ? '#f1f5f9' : '#64748b' })}
              >
                Locations
              </NavLink>
            </nav>
          </div>

          <div className="portal-header__user portal-header__user--desktop">
            <NavLink
              to="/portal/settings"
              className="portal-header__settings-icon"
              style={({ isActive }) => ({ color: isActive ? '#f1f5f9' : '#64748b' })}
              title="Settings"
              aria-label="Settings"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </NavLink>
            {user?.profilePhotoPath ? (
              <div
                className="portal-header__avatar portal-header__avatar--photo"
                style={{ backgroundImage: `url(${user.profilePhotoPath})` }}
              />
            ) : (
              <div className="portal-header__avatar portal-header__avatar--initials">
                {getInitials(user)}
              </div>
            )}
            <span className="portal-header__user-name">{user?.name || user?.email}</span>
            <button onClick={logout} className="portal-header__logout">Sign Out</button>
          </div>

          {/* Hamburger button — visible only at small breakpoint */}
          <button
            className="portal-header__hamburger"
            onClick={() => setMenuOpen(v => !v)}
            aria-label="Menu"
          >
            {menuOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18" /><path d="M6 6l12 12" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12h18" /><path d="M3 6h18" /><path d="M3 18h18" />
              </svg>
            )}
          </button>

          {/* Dropdown menu — visible only when hamburger is open */}
          {menuOpen && (
            <div className="portal-header__dropdown">
              <NavLink
                to="/portal/projects"
                className="portal-header__dropdown-link"
                style={({ isActive }) => ({ color: isActive ? '#f1f5f9' : '#94a3b8' })}
              >
                Locations
              </NavLink>
              <NavLink
                to="/portal/settings"
                className="portal-header__dropdown-link"
                style={({ isActive }) => ({ color: isActive ? '#f1f5f9' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 8 })}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                Settings
              </NavLink>
              <div className="portal-header__dropdown-divider" />
              <div className="portal-header__dropdown-user">
                {user?.profilePhotoPath ? (
                  <div
                    className="portal-header__avatar portal-header__avatar--photo"
                    style={{ backgroundImage: `url(${user.profilePhotoPath})` }}
                  />
                ) : (
                  <div className="portal-header__avatar portal-header__avatar--initials">
                    {getInitials(user)}
                  </div>
                )}
                <span style={{ color: '#94a3b8', fontSize: 13 }}>{user?.name || user?.email}</span>
              </div>
              <button onClick={logout} className="portal-header__dropdown-link portal-header__dropdown-logout">
                Sign Out
              </button>
            </div>
          )}
        </header>
      )}
      <main style={{ ...styles.main, ...(isViewer ? { overflow: 'hidden' } : {}) }}>
        <Outlet context={{ client }} />
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    background: '#0d0d0d',
    color: '#e2e8f0',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
  },
};
